import { useEffect } from 'react';
import { useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { useChanges } from '../../ui/changes.ts';
import { ErrorBox, Loading } from '../../ui/kit.tsx';
import { DeeplinkCoverage } from './DeeplinkCoverage.tsx';
import { AdSpaces, ValueMatrixView } from './DeeplinkMatrix.tsx';
import { DeeplinkParameters } from './DeeplinkParameters.tsx';
import { DeeplinkTester } from './DeeplinkTester.tsx';
import { LinkExtraction } from './LinkExtraction.tsx';
import type { NetworkDeeplinks, NetworkList } from './types.ts';

const TABS = [
  { id: 'parameters', label: 'Parameters & values' },
  { id: 'matrix', label: 'Effective values' },
  { id: 'adspaces', label: 'Ad spaces' },
  { id: 'test', label: 'Test a link' },
  { id: 'extract', label: 'Read from links' },
] as const;
type Tab = (typeof TABS)[number]['id'];

/** Deeplink generator admin, kept exactly as ported: parameters with levels, values per scope, ad spaces, plus a tester and link extraction. */
export function Deeplinks() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const networks = useOp<NetworkList>('admin.networks.list', {}, changes);
  const networkRef = query.get('network') ?? '';
  const tab = (TABS.find((t) => t.id === query.get('tab'))?.id ?? 'parameters') as Tab;
  useEffect(() => {
    const first = networks.data?.rows[0];
    if (!networkRef && first && tab !== 'adspaces') setQuery({ network: first.ref }, { replace: true });
  }, [networkRef, networks.data, tab, setQuery]);
  const detail = useOp<NetworkDeeplinks>('admin.deeplinks.network', { networkRef }, changes, Boolean(networkRef));

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Deeplinks</h1>
          <p className="muted">How each network’s tracking links are built: parameters in order, and their values per network, site, merchant and country.</p>
        </div>
      </header>
      <div className="aff-picker">
        <label>
          Network
          <select value={networkRef} onChange={(e) => setQuery({ network: e.target.value || null })} disabled={tab === 'adspaces'}>
            {networks.data?.rows.map((n) => (
              <option key={n.ref} value={n.ref}>
                {n.name}
              </option>
            ))}
          </select>
        </label>
        {detail.data ? (
          <p className="muted" style={{ margin: 0 }}>
            Link builder: <strong>{detail.data.network.generatorKey ?? 'none'}</strong>
            {detail.data.network.generatorLabel ? ` · ${detail.data.network.generatorLabel}` : ''}
          </p>
        ) : null}
      </div>
      {networkRef && tab !== 'adspaces' ? <DeeplinkCoverage networkRef={networkRef} reloadKey={changes} /> : null}
      <div className="aff-tabs" role="tablist" aria-label="Deeplink views">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setQuery({ tab: t.id === 'parameters' ? null : t.id })}>
            {t.label}
          </button>
        ))}
      </div>
      {networks.error ? <ErrorBox error={networks.error} onRetry={networks.reload} /> : null}
      {tab === 'adspaces' ? (
        <AdSpaces />
      ) : !networkRef ? (
        <Loading />
      ) : detail.error ? (
        <ErrorBox error={detail.error} onRetry={detail.reload} />
      ) : !detail.data ? (
        <Loading />
      ) : tab === 'parameters' ? (
        <DeeplinkParameters data={detail.data} />
      ) : tab === 'matrix' ? (
        <ValueMatrixView networkRef={networkRef} countries={networks.data?.countries ?? []} defaultCountry={detail.data.sites[0]?.country} />
      ) : tab === 'test' ? (
        <DeeplinkTester data={detail.data} />
      ) : (
        <LinkExtraction data={detail.data} />
      )}
    </section>
  );
}
